import { Plugin, Context } from '@nuxt/types';
import { cXense } from '@analytics/trackers';
import { Data as ArticleData } from '@root/modules/article/types/article';
import cXensePageType from '@root/modules/analytics/config/cxense/cXensePageType.config';
import events from '../config/cxense';
import interpolate from '@root/common/utils/interpolate';
import getMobileApplication from '../utils/getMobileApplication';
import { CxenseEvent } from '@root/modules/analytics/types/analytics';
import getEventByKey from '../utils/getEventByKey';
import getCustomerProfile from '../utils/getCustomerProfile';
import getDelfiHashTracker from '../utils/getDelfiHashTracker';

type CustomParameters = Record<string, unknown>;

interface CXenseArticle extends ArticleData {
  issueArticle: boolean;
}

const cXensePlugin: Plugin = async (ctx) => {
  const { app, store, route } = ctx;
  const analytics = app.$channelConfig('analytics');
  const { id, locale } = app.$channelConfig('settings');
  let firstEventSent = false;

  if (!analytics?.cxense?.siteId) {
    return;
  }

  const cX = new cXense();
  await cX.init({ uid: analytics.cxense.siteId });

  cX.push('cint', '224');
  cX.push('sync', 'adform');

  const setCustomerId = () => {
    const customerId = getCustomerProfile(app.$instanceConnector('paywall').state);
    const externalId = customerId
      ? {
          id: customerId,
          type: 'eks',
        }
      : null;
    cX.setExternalId(externalId);
  };

  const pageCustomParameters = () => {
    const routeName = app.router?.currentRoute.name;
    const hash = window.location.hash;
    const customParameters: CustomParameters = {};
    customParameters.channel = id;

    const hashParameter = getDelfiHashTracker(hash);
    if (hashParameter) {
      customParameters.delfi_link = hashParameter;
    }

    customParameters.platform = routeName?.includes('issue') ? 'Kaanest kaaneni' : 'Online vaade';
    customParameters.page_type = cXensePageType[routeName || 'other'] || cXensePageType['other'];
    customParameters.user_logged_in = store.state.customer.profile ? 'yes' : 'no';

    const application = getMobileApplication(window.navigator.userAgent);
    if (application) {
      customParameters.browser_container = application;
    }

    return customParameters;
  };

  const cXensePageView = (route: Context['route']) => {
    const isArticle = route.name === 'article' || route.name === 'issue-article';

    // Send page view of all pages except article
    if (isArticle) {
      return false;
    }

    const customParameters = pageCustomParameters();

    setCustomerId();
    cX.pageView({}, customParameters);
    firstEventSent = true;
  };

  // Initial page view after script loaded
  // If customer not logged in, push pageView event, otherwise listen once paywall state event and send pageView
  if (ctx.app.$instanceConnector('customer').state.isLoggedIn) {
    app.$eventHandler.once('paywall-state', () => cXensePageView(route));
  } else {
    cXensePageView(route);
  }

  app?.router?.afterEach((to) => {
    cXensePageView(to);
  });

  // Get cXense segments and share them to trackers
  window.addEventListener('load', async () => {
    const userId = await cX.getUserId();
    cX.push('getUserSegmentIds', {
      persistedQueryId: 'b04a959c2ea64c1ef725a6abb04ccecbbd9e6671',
      callback: async (segmentIds: string[]) => {
        store.commit('analytics/setClickEvent', {
          facebookPixel: {
            eventName: 'SYNC_SEGMENTS',
            eventData: { eventType: 'sync', data: { type: 'trackCustom', name: 'CxSegments', segmentIds } },
          },
          getSiteControl: {
            eventName: 'SYNC_SEGMENTS',
            eventData: { eventType: 'sync', data: { segmentIds, userId } },
          },
        });
      },
    });
  });

  const cXenseArticlePageView = (activeArticle: CXenseArticle) => {
    const { subscription, hasChannelAccess } = app.$instanceConnector('paywall').state;

    let customParameters = pageCustomParameters();
    const articleCustomParameters: CustomParameters = {
      is_paid_article: activeArticle.content.paywall.enabled,
      is_paid_article_and_have_paid: activeArticle.content.paywall.enabled && hasChannelAccess,
    };

    // Add customer subscription info to custom parameters
    if (subscription) {
      const { orders } = subscription;
      const hasOrders = orders?.length > 0;
      articleCustomParameters.has_user_package = hasOrders ? 'yes' : 'no';

      if (hasOrders) {
        articleCustomParameters.user_packages = orders.map((order) => order.product_code).toString();
        articleCustomParameters.product_camp = orders.map((order) => order.campaign).toString();
        articleCustomParameters.product_start = orders.map((order) => order.starting_at).toString();
        articleCustomParameters.product_type = orders.map((order) => order.type).toString();
      }
      setCustomerId();
    }
    customParameters = { ...customParameters, ...articleCustomParameters };
    cX.pageView({}, customParameters);
    firstEventSent = true;
  };

  // Watch activeArticle store state to send article page view
  store.watch(
    (state) => state.article.activeArticlePath,
    async (activeArticlePath) => {
      if (activeArticlePath && store.state.article.activeArticle) {
        const { isIssue = false } = store.state.issue;
        const cXenseArticle = { ...{ issueArticle: isIssue }, ...store.state.article.activeArticle };
        if (ctx.app.$instanceConnector('customer').state.isLoggedIn && !firstEventSent) {
          app.$eventHandler.once('paywall-state', () => cXenseArticlePageView(cXenseArticle));
        } else {
          cXenseArticlePageView(cXenseArticle);
        }
      }
    },
    {
      immediate: true,
    }
  );

  // Watch customer token change to emit cXense page view event
  app.$instanceConnector('customer').listen('token', (token) => {
    if (!token || !firstEventSent) {
      return;
    }

    if (store.state.article.activeArticle) {
      setTimeout(() => {
        const { isIssue = false } = store.state.issue;
        const cXenseArticle = { ...{ issueArticle: isIssue }, ...store.state.article.activeArticle };
        cXenseArticlePageView(cXenseArticle);
      }, 1000);
    } else {
      cXensePageView(route);
    }
  });

  // Watch analytics store eventName change
  store.watch(
    (state) => state.analytics.tracker.cXense.clickEventName,
    async (clickEventName) => {
      if (!clickEventName) {
        return;
      }
      const event = getEventByKey<CxenseEvent>(events, clickEventName, locale);

      if (event) {
        if (!store.state.analytics.tracker.cXense.clickEventData) {
          return false;
        }

        const { type, data } = store.state.analytics.tracker.cXense.clickEventData;
        const eventData = event.types[type];
        const { eventType, customParams, options } = eventData;
        const eventCustomParams = customParams ? JSON.parse(interpolate(customParams, { data, app })) : '';
        cX.sendEvent(eventType, eventCustomParams, options ? JSON.parse(options) : null);
      }
      store.commit('analytics/setClickEvent', { cXense: { eventName: null, eventData: null } });
    }
  );
};

export default cXensePlugin;
